import { ReactNode, useState } from "react";
import { css } from "@emotion/css";
import { GongShowCalculator } from "./GongShowCalculator";
import {
  DARK_BLUE,
  DARKER_BLUE,
  LIGHT_GREY,
  TURQUOISE,
} from "./colors";
import { Fields } from "./Fields";

const AccordionSection = ({
  title,
  content,
  isOpen,
  onToggle,
}: {
  title: string;
  content?: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div
    className={css(`
      border: 1px solid ${DARK_BLUE.mix(DARKER_BLUE).hex()};
      border-radius: 8px;
      background-color: ${content ? DARK_BLUE.hex() : DARKER_BLUE.hex()};
    `)}
  >
    {
      <button
        onClick={content ? onToggle : undefined}
        className={css(`
        width: 100%;
        padding: 16px;
        background: transparent;
        border: none;
        color: white;
        font-size: 16px;
        font-weight: bold;
        text-align: left;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        ${content ? "" : "pointer-events: none;"}
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `)}
      >
        {title}
        {content && <span>{isOpen ? "−" : "+"}</span>}
      </button>
    }
    {isOpen && (
      <div
        className={css(`
          padding: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
        `)}
      >
        {content}
      </div>
    )}
  </div>
);

export const RaceDescriptions = () => {
  const [openSections, setOpenSections] = useState<
    Set<number>
  >(new Set());

  const sections = [
    {
      title: "The Crapshoot",
      content: (
        <div className="race-description">
          <div>
            As the title suggests, this is a points race.
            The number of sprints will be decided by a
            diceroll, and the number of laps between sprints
            by another diceroll. For those of you familiar
            with D&D and other turn-based RPGs, we'll use
            1d4 + 2 for the laps between sprints, and 2dX
            for the number of sprints, where X varies by
            category and organizer whim.
          </div>
          <div>
            If you have no idea what any of that meant,
            basically: Cory's going to roll some dice, at
            which point you'll find out if it's 3, 4, 5, or
            6 laps between sprints. And also how many
            sprints you're doing--anywhere from 1 to 10. (If
            we come up with a 10x6, I'm going to suggest
            that Corey lie about what came up... But that's
            on him, and I'm not his supervisor.)
          </div>
          <div>Good luck picking a gear.</div>
          <Fields
            pro12="1d10 sprints"
            openWomen123="1d8 sprints"
            cat3="1d6 sprints"
          />
          <div>
            Credit to Andrew Carlberg for the seed of the
            idea here.
          </div>
        </div>
      ),
    },
    {
      title: "Kiddie Kilo",
    },
    {
      title: "Marymoor Crawl",
    },
    {
      title: "The Gong Show",
      content: (
        <div className="race-description">
          <div>
            The Gong Show is a points race. But it's not
            just any points race! It's an unknown points
            race. Only two people know when the sprints will
            be: Corey, and the officials. As a racer, you'll
            be given the number of laps and the number of
            sprints. The sprints can occur on any lap in the
            race, and there will not necessarily be points
            awarded at the finish (though ties will be
            broken at the finish).
          </div>
          <div>
            Why is it called the Gong Show, you ask? Because
            instead of the bell announcing an upcoming
            sprint, the gong will announce a{" "}
            <span style={{ fontStyle: "italic" }}>
              sprint that just happened
            </span>
            . Also, one of the sprints will be worth double
            points, as indicated by two hits of the gong.
            "But wait!" you ask. "How will I know when to
            sprint?" Well... you won't. So you should
            probably try to stay near the front.
          </div>
          <div>
            In order to do this fairly, we've built a widget
            that comes up with gong laps (and the bonus lap
            in bold).{" "}
            <a
              href="/source_code"
              target="_blank"
              className={css`
                text-decoration: none;
                color: ${TURQUOISE.hex()};

                &:hover {
                  color: ${TURQUOISE.brighten(0.3).hex()};
                }
                &:active {
                  color: ${TURQUOISE.brighten(0.6).hex()};
                }
              `}
            >
              The source code for the calculator that runs
              it is available here.
            </a>{" "}
            It will return consistent sprint laps for
            consistent seed phrases, so it's sharable
            between Cory and the officials. And it's
            auditable, if the officials keep track of the
            seed phrase that they used. To wit: it's
            pseudo-random and deterministic. Andrew Carlberg
            was supposed to code review it but never did,
            but Claude said it was fine, so I think we're
            OK.
          </div>
          <div>
            Oh, and: We've had a lot of laps taken on the
            field this year. For the gong show, lap rules
            are a litttttle bit different: If you go down a
            lap, it's -20 points. If you go up a lap, you
            get to flip a coin after the race: Heads, and
            your lap counts for +20. Tails, and it's -20.
          </div>
          <div>
            Credit: this is a Matti original, against the
            better judgement of several folks who have
            provided input. Sorry, y'all.
          </div>
          <Fields
            pro12="5 sprints, 25 laps"
            openWomen123="4 sprints, 20 laps"
            cat3="4 sprints, 20 laps"
          />
          <GongShowCalculator />
        </div>
      ),
    },
    {
      title: "The Raffle",
      content: (
        <div className="race-description">
          <div>
            We're going to start with a set of slips of
            paper for each field. The base list will be:
            Keirin, Short Points, Elim, Scratch, French
            Exit, Belgian Win and Out, Unknown Distance, and
            Mullet.
          </div>
          <div>
            After your race is assembled at the rail, we'll
            draw a slip of paper, and that's the race you're
            racing. In the event of a keirin, the draw will
            be based on your order at the rail, and we'll
            take you off the rail and run heats at that
            point.
          </div>
          <div>
            But I said{" "}
            <span style={{ fontStyle: "italic" }}>
              base list
            </span>
            , didn't I? Right, because for a $10 donation to
            JBMV, you can add a slip to any field's raffle
            drawing. Really want a keirin? Throw in a
            handlful more of those. Craving the Mullet? You
            know what to do.
          </div>
          <div>
            We're working on a way to do this online, but
            for now, plan to bring cash moneys if you want
            to add to your (or any) field's raffle tickets.
          </div>
          <div>
            But wait! How will you know what gear to run?
            Spoilers: Once more, you won't. Throw on your
            Keirin gear and hope it's not an Elim? Throw on
            your Elim gear and hope it's not a Keirin? Pick
            something in the middle and struggle no matter
            what? The choice is entirely yours; I have no
            idea what to do here.
          </div>
          <div>
            I know I promised y'all a milk race, but that
            was an empty threat. If you want to rock up to
            registration with a cooler full of various milks
            and a fat stack of cash, though, you can still
            be the change you want to see in the world...
          </div>
          <Fields
            pro12="???"
            openWomen123="???"
            cat3="???"
          />
          <div>
            Credit to Rue Tomko for pretty much this entire
            idea. (And to Andrew Carlberg for figuring out
            how to still force the milk race.)
          </div>
        </div>
      ),
    },
  ];

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div
      className={css(`
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        max-width: 600px;
        height: 100vh;
        overflow-y: auto;
        padding: 16px;
        box-sizing: border-box;
        
        .race-description {
          display: flex;
          flex-direction: column;
          gap: 12px;

          font-size: 16px;
          line-height: 21px;
          color: ${LIGHT_GREY.hex()};
        }
      `)}
    >
      {sections.map((section, index) => (
        <AccordionSection
          key={index}
          title={section.title}
          content={section.content}
          isOpen={openSections.has(index)}
          onToggle={() => toggleSection(index)}
        />
      ))}
    </div>
  );
};
